// ============================================================
// Formations — analyse des URL vidéo (TypeScript pur)
// ============================================================
// Une leçon est saisie comme une simple URL collée depuis YouTube,
// Vimeo ou un hébergement maison. Tout ce qui suit — hébergeur,
// identifiant, URL d'intégration, vignette — s'en déduit, pour que
// l'admin n'ait rien d'autre à remplir.

export type VideoProvider = 'youtube' | 'vimeo' | 'file';

export interface ParsedVideo {
  provider: VideoProvider;
  /** Identifiant chez l'hébergeur, ou null pour un fichier direct. */
  videoId: string | null;
  /** URL à poser dans l'iframe (ou dans <video> pour `file`). */
  embedUrl: string;
  /** Vignette déductible sans appel réseau, sinon null. */
  thumbnailUrl: string | null;
}

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'youtu.be',
]);

const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com']);

const FILE_EXTENSIONS = ['.mp4', '.webm', '.ogv', '.ogg', '.mov', '.m4v', '.m3u8'];

function safeUrl(raw: string): URL | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    // Une URL sans protocole (« youtu.be/xyz ») reste une intention valable.
    return new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
}

/** L'identifiant YouTube, quelle que soit la forme de l'URL. */
function youtubeId(url: URL): string | null {
  if (url.hostname === 'youtu.be') {
    const id = url.pathname.split('/').filter(Boolean)[0];
    return id ?? null;
  }
  const v = url.searchParams.get('v');
  if (v) return v;
  // /embed/ID, /shorts/ID, /live/ID, /v/ID
  const parts = url.pathname.split('/').filter(Boolean);
  const marker = parts[0];
  if (marker === 'embed' || marker === 'shorts' || marker === 'live' || marker === 'v') {
    return parts[1] ?? null;
  }
  return null;
}

/** L'identifiant Vimeo + le hash de partage des vidéos non répertoriées. */
function vimeoId(url: URL): { id: string | null; hash: string | null } {
  const parts = url.pathname.split('/').filter(Boolean);
  // player.vimeo.com/video/ID
  const start = parts[0] === 'video' ? 1 : 0;
  const id = parts[start];
  if (!id || !/^\d+$/.test(id)) return { id: null, hash: null };
  // vimeo.com/ID/HASH — le hash est requis dans l'embed d'une non répertoriée.
  const hash = parts[start + 1] ?? url.searchParams.get('h');
  return { id, hash: hash && /^[a-z0-9]+$/i.test(hash) ? hash : null };
}

/**
 * Déduit tout ce dont le lecteur a besoin depuis l'URL collée.
 * Ne lance jamais : une URL incompréhensible retombe sur `file` et
 * l'URL brute, ce que le <video> saura au moins tenter de lire.
 */
export function parseVideoUrl(raw: string): ParsedVideo {
  const url = safeUrl(raw);
  if (!url) {
    return { provider: 'file', videoId: null, embedUrl: raw.trim(), thumbnailUrl: null };
  }

  const host = url.hostname.toLowerCase();

  if (YOUTUBE_HOSTS.has(host)) {
    const id = youtubeId(url);
    if (id) {
      // youtube-nocookie : pas de cookie publicitaire posé tant que
      // l'électricien n'a pas lancé la lecture.
      const params = new URLSearchParams({ rel: '0', modestbranding: '1' });
      const start = url.searchParams.get('t') ?? url.searchParams.get('start');
      const startSeconds = start ? parseTimeParam(start) : null;
      if (startSeconds) params.set('start', String(startSeconds));
      return {
        provider: 'youtube',
        videoId: id,
        embedUrl: `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`,
        thumbnailUrl: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
      };
    }
  }

  if (VIMEO_HOSTS.has(host)) {
    const { id, hash } = vimeoId(url);
    if (id) {
      return {
        provider: 'vimeo',
        videoId: id,
        embedUrl: `https://player.vimeo.com/video/${id}${hash ? `?h=${hash}` : ''}`,
        // Vimeo ne publie pas d'URL de vignette déductible : il faudrait
        // taper son API. On laisse le composant afficher son fond dégradé.
        thumbnailUrl: null,
      };
    }
  }

  return {
    provider: 'file',
    videoId: null,
    embedUrl: url.toString(),
    thumbnailUrl: null,
  };
}

/** `90`, `1m30s`, `2h3m` → secondes. Null si illisible. */
function parseTimeParam(value: string): number | null {
  if (/^\d+$/.test(value)) return Number(value);
  const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i.exec(value);
  if (!match) return null;
  const [, h, m, s] = match;
  const total = Number(h ?? 0) * 3600 + Number(m ?? 0) * 60 + Number(s ?? 0);
  return total || null;
}

/** L'URL pointe-t-elle vers un binaire lisible par <video> ? */
export function looksLikeVideoFile(raw: string): boolean {
  const url = safeUrl(raw);
  if (!url) return false;
  const path = url.pathname.toLowerCase();
  return FILE_EXTENSIONS.some((ext) => path.endsWith(ext));
}

/** `754` → `12:34`. `null` → chaîne vide (le composant masque le badge). */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** `"12:34"` ou `"754"` → 754 secondes. Null si illisible. */
export function parseDurationInput(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const parts = trimmed.split(':').map((p) => p.trim());
  if (parts.some((p) => !/^\d+$/.test(p))) return null;
  return parts.reduce((acc, part) => acc * 60 + Number(part), 0) || null;
}
